import { ActionIcon, Container, Flex, Menu, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import {
  IconDots,
  IconDownload,
  IconEdit,
  IconNumber123,
  IconPlus,
  IconRefresh,
  IconSend,
  IconTrash,
  IconZoomIn,
} from '@tabler/icons-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import LinkButton from 'common/LinkButton'
import dayjs from 'dayjs'
import { deliveryCompaniesQuery, useDeleteOrder, useExportOrderToPdf, useOrders } from 'hooks/orders'
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Order } from 'types/orders'

export default function OrdersTable() {
  const navigate = useNavigate()
  const { data: deliveryCompanies } = useSuspenseQuery(deliveryCompaniesQuery)
  const exportOrderToPdf = useExportOrderToPdf()
  const deleteOrder = useDeleteOrder()

  function downloadOrderPdf(orderId: number) {
    exportOrderToPdf.mutate(orderId)
  }

  function openDeleteModal(orderNumber: number) {
    const order = memoizedData.data.find((o) => o.id === orderNumber)
    if (!order) return
    modals.openConfirmModal({
      title: 'Usuń zamówienie',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć zamówienie <b>Z-{order.id}</b>? Nie będzie można cofnąć tej operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń zamówienie', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteOrder.mutateAsync(order.id),
    })
  }

  function openDispatchParcelsModal() {
    modals.openContextModal({
      modal: 'dispatchParcels',
      title: 'Nadawanie przesyłek',
      centered: true,
      size: 'lg',
      innerProps: {
        selectedOrderIds: allSelectedRows.map((o) => o.id),
      },
      keepMounted: false,
    })
  }

  function openGetOrderNumberModal() {
    modals.openContextModal({
      modal: 'getNumberForOrder',
      title: 'Pobierz numer dla zamówienia',
      centered: true,
      size: 'md',
      innerProps: {},
    })
  }

  const [globalFilter, setGlobalFilter] = useState<string | undefined>('')
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const selectedRowDataRef = useRef<Record<string, Order>>({})
  const [allSelectedRows, setAllSelectedRows] = useState<Array<Order>>([])

  const { data, isLoading, isError, error, isFetching, refetch } = useOrders({
    columnFilters,
    pagination,
    sorting,
    globalFilter,
  })
  const memoizedData = useMemo(() => data ?? { data: [], total: 0 }, [JSON.stringify(data)])

  useEffect(() => {
    const currentData = memoizedData.data
    const nextSelectedData: Record<string, Order> = {}
    for (const id of Object.keys(rowSelection)) {
      if (rowSelection[id]) {
        const rowFromCurrentPage = currentData.find((row) => row.id.toString() === id)
        nextSelectedData[id] = rowFromCurrentPage ?? selectedRowDataRef.current[id]
      }
    }
    selectedRowDataRef.current = nextSelectedData
    setAllSelectedRows(Object.values(nextSelectedData).filter(Boolean))
  }, [rowSelection, memoizedData.data])

  const columns = useMemo<Array<MRT_ColumnDef<Order>>>(
    () => [
      {
        accessorKey: 'id',
        header: 'Numer zamówienia',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po numerze zamówienia',
        },
        Cell: ({ cell }) => {
          const id = cell.getValue<number>()
          return <LinkButton to={`details/${id}`}>Z-{id.toString()}</LinkButton>
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Data utworzenia',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date>()
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
      },
      {
        accessorKey: 'firstName',
        header: 'Zamawiający',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po zamawiającym',
        },
        Cell: ({ row }) => {
          const { firstName, lastName } = row.original
          return `${firstName} ${lastName}`
        },
      },
      {
        accessorKey: 'totalPrice',
        header: 'Kwota',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po kwocie',
        },
        size: 110,
        Cell: ({ cell }) => {
          const totalPrice = cell.getValue<number>()
          return `${totalPrice.toFixed(2)} zł`
        },
      },
      {
        accessorKey: 'shipmentType.deliveryCompanyName',
        header: 'Kurier',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po kurierze',
        },
        size: 130,
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: deliveryCompanies
            .filter((company) => company.isActive && company.shipmentTypes.some((type) => type.isActive))
            .map((company) => ({ label: company.name, value: company.id })),
        },
      },
      {
        accessorKey: 'dispatchedAt',
        header: 'Data wysyłki',
        filterVariant: 'date',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po dacie',
        },
        Cell: ({ cell }) => {
          const dispatchedAt = cell.getValue<Date | null>()
          return dispatchedAt ? dayjs(dispatchedAt).format('D MMMM HH:mm') : ''
        },
        size: 180,
      },
    ],
    [deliveryCompanies]
  )

  const table = useMantineReactTable({
    columns,
    data: memoizedData.data,
    layoutMode: 'grid',
    rowCount: memoizedData.total,
    getRowId: (row, index) => (row.id ? row.id.toString() : index.toString()),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      globalFilter,
      columnFilters,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
      rowSelection,
    },
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: true,
    },
    positionGlobalFilter: 'left',
    mantineSearchTextInputProps: {
      placeholder: `Numer zamówienia, numer telefonu, email...`,
      styles: { input: { minWidth: '320px' } },
    },
    enableGlobalFilterModes: true,
    enableSorting: true,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableBottomToolbar: true,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
    enableTopToolbar: true,
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj nowe zamówienie">
          <ActionIcon size="lg" variant="subtle" onClick={() => navigate({ to: 'new' })} ml="auto" mr={4}>
            <IconPlus />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Pobierz numer dla zamówienia">
          <ActionIcon size="lg" variant="subtle" onClick={openGetOrderNumberModal} ml="auto" mr={4}>
            <IconNumber123 />
          </ActionIcon>
        </Tooltip>
        {allSelectedRows.length > 0 && (
          <Tooltip label={`Nadaj przesyłki dla ${allSelectedRows.length} wybranych zamówień`}>
            <ActionIcon size="lg" variant="filled" color="brand" onClick={openDispatchParcelsModal} ml="auto" mr={4}>
              <IconSend />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Odśwież dane">
          <ActionIcon size="lg" variant="subtle" color="gray" onClick={() => refetch()}>
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error})`,
        }
      : undefined,
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['10', '15', '20'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
      },
    },
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Pobierz">
          <ActionIcon radius="xl" color="brand" variant="light" onClick={() => downloadOrderPdf(row.original.id)}>
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>
        <Menu withArrow arrowSize={10} position="bottom-end" withinPortal offset={0} arrowOffset={8}>
          <Menu.Target>
            <ActionIcon radius="xl" variant="subtle" color="gray">
              <IconDots size={18} stroke={2} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconZoomIn size={16} stroke={1.5} />}
              onClick={() => navigate({ to: `details/${row.original.id}` })}
            >
              Zobacz szczegóły
            </Menu.Item>
            <Menu.Item
              leftSection={<IconEdit size={16} stroke={1.5} />}
              onClick={() => navigate({ to: `edit/${row.original.id}` })}
            >
              Edytuj
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() => openDeleteModal(row.original.id)}
            >
              Usuń
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    ),
  })

  return <MantineReactTable table={table} />
}
